import { useDisclosure, useToast } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { RanaButton, TenantContext } from "@nens/ranalib";

import { getProjectTabQueryParam } from "@/features/project";
import { useFileDirectoryQueryDirectlyWithProjectId } from "@/queries/query-file";
import { SchematisationWithId } from "@/queries/query-hcc-schematisation";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

import { useImportSchematisation } from "./api/import-schematisation";
import { SchematisationProjectImportModalFromCatalogue } from "./components/schematisation-project-import-modal-from-catalogue";
import { SchematisationProjectImportModalFromProject } from "./components/schematisation-project-import-modal-from-project";
import { SchematisationProjectImportOverwriteModal } from "./components/schematisation-project-import-overwrite-modal";
import { BaseImportArgs, OnImportFunc } from "./types/on-import";

type SchematisationProjectImportButtonFromCatalogueProps = {
  from: "catalogue";
  schematisation: SchematisationWithId;
  revisionId: number | undefined;
};
type SchematisationProjectImportButtonFromProjectProps = {
  from: "project";
  project: ProjectRead;
  path: string;
};

export type SchematisationProjectImportButtonProps =
  | SchematisationProjectImportButtonFromCatalogueProps
  | SchematisationProjectImportButtonFromProjectProps;

export function SchematisationProjectImportButton(props: SchematisationProjectImportButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <RanaButton variant="primary" onClick={onOpen}>
        <FormattedMessage id="button.add_to_project" defaultMessage="Add to project" />
      </RanaButton>

      <SchematisationProjectImportModals {...props} isOpen={isOpen} onClose={onClose} />
    </>
  );
}

type SchematisationProjectImportModalsProps = SchematisationProjectImportButtonProps & {
  isOpen: boolean;
  onClose: () => void;
};

export function SchematisationProjectImportModals({
  isOpen,
  onClose,
  ...props
}: SchematisationProjectImportModalsProps) {
  const toast = useToast();
  const intl = useIntl();
  const { tenant } = useContext(TenantContext);

  const { mutate, isMutating } = useImportSchematisation();
  const fetchProjectFiles = useFileDirectoryQueryDirectlyWithProjectId();

  interface ShowOverwriteFor extends BaseImportArgs {
    existingFileIds: string[];
  }
  const [showOverwriteModalFor, setShowOverwriteModalFor] = useState<ShowOverwriteFor | null>(null);

  const addToProject: OnImportFunc = ({ project, schematisation, revisionId, path }) => {
    mutate(
      {
        projectId: project.id,
        path,
        schematisationId: schematisation.id,
        name: schematisation.name,
        revisionId,
      },
      {
        onSuccess: (refrence) => {
          toast({
            title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
            description: intl.formatMessage(
              {
                id: "success.add_schematisation",
                defaultMessage: "Model schematisation addded! {link}",
              },
              {
                link: (
                  <Text
                    as="a"
                    textDecoration="underline"
                    href={`${window.location.origin}/${tenant}/projects/${
                      project.slug
                    }?${getProjectTabQueryParam("files")}&${convertFileIdToQueryParams(
                      refrence.id
                    )}`}
                  >
                    {intl.formatMessage({
                      id: "success.import_schematisation.link",
                      defaultMessage: "View",
                    })}
                  </Text>
                ),
              }
            ),
          });
          onClose();
          setShowOverwriteModalFor(null);
        },
      }
    );
  };

  if (showOverwriteModalFor) {
    const { project, path, schematisation, revisionId, existingFileIds } = showOverwriteModalFor;
    return (
      <SchematisationProjectImportOverwriteModal
        path={path}
        selectedName={schematisation.name}
        existingFileIds={existingFileIds}
        isMutating={isMutating}
        onClose={() => setShowOverwriteModalFor(null)}
        onImport={(name) => {
          addToProject({
            project,
            schematisation: { ...schematisation, name },
            revisionId,
            path,
          });
        }}
      />
    );
  }

  const onImport: OnImportFunc = async (args) => {
    const { project, path, schematisation } = args;
    const { data: files, isError } = await fetchProjectFiles(project.id, path);
    if (isError) {
      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
        description: intl.formatMessage({
          id: "error.fetch_project_files",
          defaultMessage: "Failed to fetch project files.",
        }),
      });
      return;
    }
    const fileIds = files.items.map((file) => file.id);

    const alreadyExists = fileIds.some(
      (fileId) => getNameFromFileId(fileId) === schematisation.name
    );

    if (alreadyExists) {
      setShowOverwriteModalFor({ ...args, existingFileIds: fileIds });
      return;
    }

    addToProject(args);
  };

  if (isOpen) {
    if (props.from === "catalogue") {
      return (
        <SchematisationProjectImportModalFromCatalogue
          onClose={onClose}
          isMutating={isMutating}
          onImport={(importProps) => onImport({ ...importProps, ...props })}
        />
      );
    }

    if (props.from === "project") {
      return (
        <SchematisationProjectImportModalFromProject
          onClose={onClose}
          isMutating={isMutating}
          onImport={(importProps) => onImport({ ...importProps, ...props })}
        />
      );
    }
  }

  return null;
}
